import { PointerEvent as ReactPointerEvent, useRef, useState } from 'react';

import { Button } from '@/components/core/Button';
import { Icon } from '@/components/core/Icon';
import { DropField } from '@/components/core/Uploader/DropField';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';

import { accountImageConfig, type AccountProfileVariant } from './accountProfileVariant.ts';

const ACCEPTED_IMAGE_TYPES = {
    'image/jpeg': [],
    'image/gif': [],
    'image/png': [],
};

const CROP_OUTPUT_SIZE = 250;
const IMAGE_FRAME_HEIGHT_CLASS = 'tw-h-[11.75rem]';

interface CropBox {
    x: number;
    y: number;
    size: number;
}

interface ProfileImageFieldProps {
    variant: AccountProfileVariant;
    canEdit: boolean;
    hasImage: boolean;
    imageUrl?: string;
    onUploaded: () => void | Promise<void>;
}

function cropToBlob(image: HTMLImageElement, crop: CropBox): Promise<Blob> {
    const scaleX = image.naturalWidth / image.clientWidth;
    const scaleY = image.naturalHeight / image.clientHeight;
    const canvas = document.createElement('canvas');
    canvas.width = CROP_OUTPUT_SIZE;
    canvas.height = CROP_OUTPUT_SIZE;
    const context = canvas.getContext('2d');
    if (!context) {
        return Promise.reject(new Error('Could not crop the profile image'));
    }
    context.drawImage(
        image,
        crop.x * scaleX,
        crop.y * scaleY,
        crop.size * scaleX,
        crop.size * scaleY,
        0,
        0,
        CROP_OUTPUT_SIZE,
        CROP_OUTPUT_SIZE,
    );
    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (blob) {
                resolve(blob);
                return;
            }
            reject(new Error('Could not crop the profile image'));
        }, 'image/png');
    });
}

const ProfileImageCrop = ({
    src,
    uploadUrl,
    fileName,
    idPrefix,
    onCancel,
    onUploaded,
}: {
    src: string;
    uploadUrl: string;
    fileName: string;
    idPrefix: string;
    onCancel: () => void;
    onUploaded: () => void | Promise<void>;
}) => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const imageRef = useRef<HTMLImageElement>(null);
    const cropRef = useRef<CropBox | null>(null);
    const [crop, setCrop] = useState<CropBox | null>(null);
    const [uploading, setUploading] = useState(false);

    const updateCrop = (next: CropBox) => {
        cropRef.current = next;
        setCrop(next);
    };

    const onImageLoad = () => {
        const image = imageRef.current;
        if (!image) {
            return;
        }
        const size = Math.min(image.clientWidth, image.clientHeight);
        updateCrop({
            x: (image.clientWidth - size) / 2,
            y: (image.clientHeight - size) / 2,
            size,
        });
    };

    const onPointerDown = (mode: 'move' | 'resize') => (event: ReactPointerEvent<HTMLDivElement>) => {
        if (!cropRef.current || !imageRef.current) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        const origin = cropRef.current;
        const startX = event.clientX;
        const startY = event.clientY;

        const move = (pointerEvent: PointerEvent) => {
            const image = imageRef.current;
            if (!image) {
                return;
            }
            const dx = pointerEvent.clientX - startX;
            const dy = pointerEvent.clientY - startY;
            const boundsWidth = image.clientWidth;
            const boundsHeight = image.clientHeight;

            if (mode === 'move') {
                updateCrop({
                    size: origin.size,
                    x: Math.min(Math.max(0, origin.x + dx), boundsWidth - origin.size),
                    y: Math.min(Math.max(0, origin.y + dy), boundsHeight - origin.size),
                });
                return;
            }

            const maxSize = Math.min(boundsWidth - origin.x, boundsHeight - origin.y);
            const size = Math.min(Math.max(24, origin.size + Math.max(dx, dy)), maxSize);
            updateCrop({ x: origin.x, y: origin.y, size });
        };
        const stop = () => {
            window.removeEventListener('pointermove', move);
            window.removeEventListener('pointerup', stop);
            window.removeEventListener('pointercancel', stop);
        };
        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', stop);
        window.addEventListener('pointercancel', stop);
    };

    const upload = async () => {
        const image = imageRef.current;
        const currentCrop = cropRef.current;
        if (!image || !currentCrop) {
            return;
        }
        setUploading(true);
        try {
            const blob = await cropToBlob(image, currentCrop);
            const body = new FormData();
            body.append('file', blob, fileName);
            const response = await fetch(uploadUrl, { method: 'POST', body });
            if (!response.ok) {
                throw new Error('Profile image upload failed');
            }
            await onUploaded();
        } catch {
            toast.error(t('SAVE_ERROR_MESSAGE'));
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="tw-flex tw-w-full tw-flex-col tw-gap-3">
            <div className={`tw-flex tw-w-full tw-items-center tw-justify-center tw-overflow-hidden tw-border tw-border-solid tw-border-primary tw-bg-white tw-p-4 ${IMAGE_FRAME_HEIGHT_CLASS}`}>
                <div className="tw-relative tw-max-h-full tw-max-w-full">
                    <img
                        ref={imageRef}
                        src={src}
                        alt=""
                        onLoad={onImageLoad}
                        className="tw-block tw-max-h-[9.75rem] tw-max-w-full tw-object-contain"
                    />
                    {crop && (
                        <div
                            role="application"
                            aria-label={t('UPLOAD.CHANGE_IMAGE')}
                            className="tw-absolute tw-z-0 tw-cursor-move tw-border-2 tw-border-solid tw-border-primary tw-bg-transparent"
                            style={{ left: crop.x, top: crop.y, width: crop.size, height: crop.size }}
                            onPointerDown={onPointerDown('move')}
                        >
                            <div
                                aria-hidden="true"
                                className="tw-absolute tw-bottom-0 tw-right-0 tw-h-3 tw-w-3 tw-cursor-nwse-resize tw-bg-fill-primary"
                                onPointerDown={onPointerDown('resize')}
                            />
                        </div>
                    )}
                </div>
            </div>
            <div className="tw-relative tw-z-10 tw-flex tw-w-full tw-flex-col tw-gap-2">
                <Button id={`${idPrefix}_upload_submit`} className="tw-w-full" disabled={uploading || !crop} onClick={upload}>
                    {t('UPLOAD_IMAGE')}
                </Button>
                <Button id={`${idPrefix}_upload_cancel_button`} className="tw-w-full" variant="secondary" disabled={uploading} onClick={onCancel}>
                    {t('CANCEL')}
                </Button>
            </div>
        </div>
    );
};

const ProfileImageField = ({
    variant,
    canEdit,
    hasImage,
    imageUrl,
    onUploaded,
}: ProfileImageFieldProps) => {
    const { uploadUrl, fileName, placeholderIcon, idPrefix, className } = accountImageConfig(variant);
    const { t } = useTranslation();
    const { toast } = useToast();
    const [showUpload, setShowUpload] = useState(false);
    const [imageToCrop, setImageToCrop] = useState<string | null>(null);
    const [previewNonce, setPreviewNonce] = useState(0);

    const hidePlaceholder = showUpload;
    const previewUrl = imageUrl && previewNonce
        ? `${imageUrl}${imageUrl.includes('?') ? '&' : '?'}v=${previewNonce}`
        : imageUrl;

    const closeUpload = () => {
        setShowUpload(false);
        setImageToCrop(null);
    };

    const onFilesAdded = (files: File[]) => {
        const file = files[0];
        if (!file) {
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            setImageToCrop(typeof reader.result === 'string' ? reader.result : null);
        };
        reader.readAsDataURL(file);
    };

    return (
        <div className={`tw-relative tw-w-full tw-shrink-0 md:tw-w-64 ${className ?? ''}`}>
            {!showUpload && (
                <div className={`tw-flex tw-flex-col tw-border tw-border-solid tw-border-primary tw-bg-white tw-p-4 ${IMAGE_FRAME_HEIGHT_CLASS}`}>
                    <div className="tw-flex tw-min-h-0 tw-flex-1 tw-items-center tw-justify-center">
                        {!hasImage && !hidePlaceholder && (
                            <Icon icon={placeholderIcon} variant="secondary" className="tw-h-24 tw-w-24" />
                        )}

                        {((canEdit && previewUrl && hasImage) || (!canEdit && imageUrl)) && (
                            <img
                                src={canEdit ? previewUrl : imageUrl}
                                alt=""
                                className="tw-max-h-full tw-max-w-full tw-object-contain"
                            />
                        )}
                    </div>

                    {canEdit && (
                        <Button variant="outline" className="tw-relative tw-z-10 tw-mt-4 tw-w-full tw-shrink-0" onClick={() => setShowUpload(true)}>
                            {t('UPLOAD.CHANGE_IMAGE')}
                        </Button>
                    )}
                </div>
            )}

            {canEdit && showUpload && !imageToCrop && (
                <div className="tw-flex tw-w-full tw-flex-col tw-gap-3 [&_.uploader-drop-area]:!tw-h-[11.75rem] [&_.uploader-drop-area]:!tw-gap-3 [&_.uploader-drop-area]:!tw-p-3">
                    <DropField
                        accept={ACCEPTED_IMAGE_TYPES}
                        isMultiple={false}
                        onFilesAdded={onFilesAdded}
                    />
                    <Button id={`${idPrefix}_upload_cancel_button`} className="tw-relative tw-z-10 tw-w-full" variant="secondary" onClick={closeUpload}>
                        {t('CANCEL')}
                    </Button>
                </div>
            )}

            {canEdit && showUpload && imageToCrop && (
                <ProfileImageCrop
                    src={imageToCrop}
                    uploadUrl={uploadUrl}
                    fileName={fileName}
                    idPrefix={idPrefix}
                    onCancel={closeUpload}
                    onUploaded={async () => {
                        await onUploaded();
                        toast.success(t('UPLOAD_SUCCESS_MESSAGE'));
                        setPreviewNonce(Date.now());
                        closeUpload();
                    }}
                />
            )}
        </div>
    );
};

export { ProfileImageField };
